import { useState } from "react";
import * as XLSX from "xlsx";
import { Modal, Button, Label, TextInput } from "flowbite-react"; // <-- Importa os componentes corretos
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import {
  StockInventory,
  StockInventoryReportItem,
} from "../Types/StockInventory.types";
import {
  getStockInventoryReportDetails,
  confirmInventoryCount,
} from "@/common/services/stock-inventory.api";
import Loading from "@/common/components/ui/loading/Loading";

type Props = {
  inventory: StockInventory;
  onClose: () => void;
  onSave: () => void;
};

interface CountItem extends StockInventoryReportItem {
  countedQuantity: number | string;
}
type ExcelRow = {
  "ID do Produto": string | number;
  Código?: string;
  Nome?: string;
  Contagem: number;
};
export default function ModalConfirmCount({
  inventory,
  onClose,
  onSave,
}: Props) {
  const { notifyError, notifySuccess } = useToastContext();
  const [items, setItems] = useState<CountItem[]>([]);
  const [view, setView] = useState<"initial" | "list">("initial");
  const [isLoading, setIsLoading] = useState(false);

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: ExcelRow[] = XLSX.utils.sheet_to_json(worksheet);
        const parsedItems = json.map((row) => ({
          productId: row["ID do Produto"]?.toString(),
          code: row["Código"]?.toString() || "N/A",
          name: row["Nome"]?.toString() || "Produto desconhecido",
          quantity: 0,
          quantityStock: 0,
          diference: 0,
          countedQuantity: row["Contagem"] ?? "",
        }));
        setItems(parsedItems);
        setView("list");
      } catch (err) {
        notifyError("Erro ao ler o arquivo Excel. Verifique o formato.");
      } finally {
        setIsLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleManualFill = async () => {
    setIsLoading(true);
    try {
      const result = await getStockInventoryReportDetails(inventory._id);
      if (result.ok && result.data) {
        const manualItems = result.data.map((item) => ({
          ...item,
          countedQuantity: "",
        }));
        setItems(manualItems);
        setView("list");
      } else {
        notifyError("Falha ao buscar itens para contagem manual.");
      }
    } catch (error) {
      notifyError("Ocorreu um erro.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantityChange = (productId: string, value: string) => {
    const newItems = items.map((item) => {
      if (item.productId === productId) {
        const numValue = parseInt(value, 10);
        const finalValue = isNaN(numValue) ? "" : Math.max(0, numValue);
        return { ...item, countedQuantity: finalValue };
      }
      return item;
    });
    setItems(newItems);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const isAllFilled = items.every(
      (item) =>
        item.countedQuantity !== "" && !isNaN(Number(item.countedQuantity))
    );
    if (!isAllFilled) {
      notifyError(
        "Todos os itens devem ter uma contagem preenchida (pode ser 0)."
      );
      return;
    }
    setIsLoading(true);
    try {
      const payload = items.map((item) => ({
        productId: item.productId,
        quantity: Number(item.countedQuantity),
      }));
      const result = await confirmInventoryCount(inventory._id, payload);
      if (result.ok) {
        notifySuccess("Contagem confirmada com sucesso!");
        onSave();
      } else {
        notifyError(result.message || "Falha ao confirmar contagem.");
      }
    } catch (error) {
      notifyError("Ocorreu um erro ao enviar os dados.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={true} onClose={onClose} size="5xl">
      <Modal.Header>
        <h2 className="text-black">Confirmar Contagem do Inventário</h2>
      </Modal.Header>

      {view === "initial" && (
        <Modal.Body>
          {isLoading ? (
            <Loading />
          ) : (
            <div className="flex justify-center gap-4">
              <Button
                className="cursor-pointer"
                as="label"
                htmlFor="excel-upload"
              >
                Carregar Arquivo Excel
              </Button>
              <input
                id="excel-upload"
                type="file"
                accept=".xlsx, .xls"
                className="hidden"
                onChange={handleExcelUpload}
              />
              <Button color="success" onClick={handleManualFill}>
                Preencher Manualmente
              </Button>
            </div>
          )}
        </Modal.Body>
      )}

      {view === "list" && (
        <form onSubmit={handleSubmit}>
          <Modal.Body className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <Loading />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((item) => (
                  <div key={item.productId}>
                    <Label
                      className="text-black"
                      color={"black"}
                      htmlFor={item.productId}
                      value={`${item.code} - ${item.name}`}
                    />
                    <TextInput
                      id={item.productId}
                      type="number"
                      color={"black"}
                      value={item.countedQuantity}
                      onChange={(e) =>
                        handleQuantityChange(item.productId, e.target.value)
                      }
                      placeholder="Contagem..."
                      min="0"
                      required
                    />
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" isProcessing={isLoading} disabled={isLoading}>
              Confirmar Contagem
            </Button>
            <Button color="red" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
}
